import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';

import Breakpoints from 'lib/Breakpoints';
import { layout as servicesLayoutPropType } from 'lib/CustomPropTypes/services';
import { children as childrenPropType } from 'lib/CustomPropTypes';
import { TVE_CANONICAL_ROUTE } from 'lib/tve';
import { isTelemundoVertical } from 'lib/vertical';

import Ad from 'components/Ad';
import ErrorBoundary from 'components/ErrorBoundary';
import Marquee from 'components/Marquee';
import ProgramGuide from 'components/ProgramGuide';

import ServiceHeader from '../Header';
import ServiceFooter from '../Footer';

import styles from './styles.module.scss';

const mapStateToProps = ({
  article,
  router,
  shared,
  front,
}) => {
  const adsEnabled = get(article, ['content', 0, 'adsEnabled'], true);
  const ecommerceEnabled = get(article, ['content', 0, 'ecommerceEnabled'], false);
  const nativeAd = get(article, 'isNativeAd', false);
  const adsDisabledByCurator = get(front, 'curation.adsDisabled', false);
  return {
    adsEnabled: adsEnabled && !ecommerceEnabled && !nativeAd,
    currentPath: router.path,
    pageView: shared.pageView,
    vertical: shared.vertical,
    adsDisabledByCurator,
  };
};

class HeaderAndFooter extends React.Component {
  static propTypes = {
    adsEnabled: PropTypes.bool,
    adsDisabledByCurator: PropTypes.bool,
    children: childrenPropType,
    currentPath: PropTypes.string.isRequired,
    layout: servicesLayoutPropType.isRequired,
    pageView: PropTypes.string,
    vertical: PropTypes.string.isRequired,
  };

  static defaultProps = {
    adsEnabled: true,
    adsDisabledByCurator: false,
    children: null,
    pageView: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      topBannerAdRendered: false,
      topBannerAdUnstuck: false,
      topBannerHeight: 0,
    };
  }

  componentDidMount() {
    this.waitForAdFor(4000);
    this.checkFocus();
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps({ pageView: nextPageType }) {
    const { pageView: currentPageType } = this.props;

    // When transporting from a video or slideshow to a front, force the banner ad to be hidden
    if (['video', 'slideshow'].includes(currentPageType) && nextPageType === 'front') {
      this.forceHideBannerAd = true;
    }
  }

  waitForAdFor = (time) => setTimeout(this.checkForAd, time);

  checkFocus = () => {
    if (typeof window === 'undefined') return;

    this.setState({
      topBannerHeight: (!this.shouldShowBannerAd() || !this.isFocused()) ? 0 : 130,
    });
  }

  checkForAd = () => {
    if (!this.adRendered) {
      this.setState({ bannerNotRendered: true, topBannerHeight: 0 });
    }
  }

  bannerAdHandler = (enigmaAd, { EVENTS }) => {
    this.enigmaAd = this.enigmaAd || enigmaAd;

    this.enigmaAd.on(EVENTS.RENDER, (event) => {
      if (!event || event.empty) {
        return;
      }
      this.adRendered = true;
      const { topBannerAdUnstuck } = this.state;

      this.setState({
        topBannerHeight: (topBannerAdUnstuck || Breakpoints.isS()) ? 0
          : this.enigmaAd.element.clientHeight + 40,
        topBannerAdRendered: true,
      });

      setTimeout(() => {
        this.setState({
          topBannerHeight: 0,
          topBannerAdUnstuck: true,
        });
      }, 3000);
    });
  };

  isFocused = () => typeof document === 'undefined' || document.hasFocus();

  shouldShowBannerAd() {
    const {
      adsDisabledByCurator,
      adsEnabled,
      currentPath,
      pageView,
      vertical,
    } = this.props;

    // Blanket disable for video and slideshow pages
    if (this.forceHideBannerAd || ['video', 'slideshow'].includes(pageView)) {
      return false;
    }
    // Disable on MSNBC TVE page
    if (vertical === 'msnbc' && currentPath.includes(`/${TVE_CANONICAL_ROUTE}`)) {
      return false;
    }

    // [1] If a vertical has no key in `disableAd`, the bannerAd will be enabled for all pageViews
    // [2] An entry of `all` will disable the bannerAd on all pageViews for the given vertical
    // Examples:
    // Disable on Mach fronts and slideshows -- mach: ['front', 'slideshow']
    // Disable on all Mach page types -- mach: ['all']
    const disableAd = {
      leftfield: ['all'],
    };

    const disabledOnPages = disableAd[vertical];

    const isArticleWithAdsEnabled = pageView === 'article' && adsEnabled;
    const isAdsEnabledByCurator = !adsDisabledByCurator;
    const isFrontAndAdsEnabledByCurator = pageView === 'front' && isAdsEnabledByCurator;
    const showBannerAd = isArticleWithAdsEnabled || isFrontAndAdsEnabledByCurator;

    // [1] For verticals without keys in `disableAd`
    if (typeof disabledOnPages === 'undefined' && showBannerAd) {
      return true;
    }

    // [2] For verticals marked `all` or with specific page types
    if (disabledOnPages && (disabledOnPages.includes('all') || disabledOnPages.includes(pageView))) {
      return false;
    }

    return showBannerAd;
  }

  render() {
    const {
      bannerNotRendered,
      topBannerAdRendered,
      topBannerAdUnstuck,
      topBannerHeight,
    } = this.state;

    const {
      adsEnabled,
      children,
      currentPath,
      layout,
      pageView,
      vertical,
    } = this.props;

    // Logic here is protect against banner not showing on the homepage, as a result
    // of hitting back on the browser.

    const bannerAdClasses = classNames(
      styles.bannerAd,
      'ad-container topbannerAd',
      {
        [styles.static]: topBannerAdUnstuck,
        [styles.adRendered]: topBannerAdRendered,
        // The header footer service listens for a class change
        // to recalc its position. This class serves to trigger that
        triggerClass: bannerNotRendered,
      },
    );

    const topbannerPlaceholderStyles = {
      height: `${topBannerHeight}px`,
    };

    if (topBannerAdUnstuck) {
      topbannerPlaceholderStyles.display = 'none';
    }

    return (
      <>
        <ErrorBoundary>
          {this.shouldShowBannerAd() && (
            <div className={bannerAdClasses}>
              <div
                className={styles.adBackground}
                style={{ height: `${topBannerHeight}px` }}
              />
              <Ad
                handler={this.bannerAdHandler}
                slot="topbanner"
              />
            </div>
          )}

          <div
            className="db-m dn"
            style={topbannerPlaceholderStyles}
          />
        </ErrorBoundary>

        <ErrorBoundary>
          <Marquee />
        </ErrorBoundary>

        <ErrorBoundary>
          <ServiceHeader
            adsEnabled={adsEnabled}
            currentPath={currentPath}
            header={layout.header}
            pageView={pageView}
            vertical={vertical}
          />
        </ErrorBoundary>

        {children}

        {isTelemundoVertical(vertical) && (
          <ErrorBoundary>
            <ProgramGuide url="https://master.d2gnmmimbvzgnk.amplifyapp.com/" />
          </ErrorBoundary>
        )}

        <ErrorBoundary>
          <ServiceFooter footer={layout.footer} />
        </ErrorBoundary>
      </>
    );
  }
}

export { HeaderAndFooter as UnwrappedHeaderAndFooter };

const WrappedHeaderAndFooter = connect(mapStateToProps)(HeaderAndFooter);
export { WrappedHeaderAndFooter as HeaderAndFooter };
